import { createFileRoute } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import { z } from 'zod';

import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { DEFAULT_SORTING, TableType } from '@/components/Filters/filter-constants';
import { Sources } from '@/components/Sources/Sources';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';

import type { DashboardStaticData } from './route';

const librarySearchSchema = z.object({
  pageSize: fallback(z.number(), DEFAULT_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  pageIndex: fallback(z.number(), 0).default(0),
  generateRowIds: fallback(z.array(z.string()), []).catch([]),
  types: fallback(z.array(z.enum([TableType.CODEBASE, TableType.PDF])), [TableType.CODEBASE, TableType.PDF]).default([
    TableType.CODEBASE,
    TableType.PDF,
  ]),

  search: searchParam().default(''),
  columnFilters: fallback(
    z.array(
      z.object({
        id: z.string(),
        value: z.unknown().refine((x) => x !== undefined, 'Required'),
      })
    ),
    []
  ).default([]),
  sorting: fallback(
    z
      .array(
        z
          .object({
            id: z.string(),
            desc: z.boolean(),
          })
          .required()
      )
      .min(1),
    DEFAULT_SORTING
  ).default(DEFAULT_SORTING),
  codebaseSettingsId: searchParam().optional(),
  // Controls whether the onboarding modal is open
  getStarted: z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean()).optional(),
  // Controls whether the generate docs helper popover is open
  generateDoc: z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean()).optional(),
  // Controls whether the enable export helper popover is open
  enableExport: z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean()).optional(),
});

const staticData: DashboardStaticData = {
  navbarTitle: 'Sources',
};

export const Route = createFileRoute('/$organizationName/_dashboard/sources')({
  component: SourcesPage,
  validateSearch: zodValidator(librarySearchSchema),
  staticData,
});

// Body-only — the _dashboard layout provides the LayoutDefault chrome, header
// (incl. the SlocWarning + NewAssetAction in the header `right` slot), and SourcesNav.
function SourcesPage() {
  const { organizationDisplayName } = useUser();

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Sources`;
  }, [organizationDisplayName]);

  return (
    <div className="space-y-4">
      <Sources />
    </div>
  );
}
